import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type { InsertObligationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertObligationDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';
import type { InsertObligationInput } from '../scheduledEntityInputs';

type InsertObligationTRPCInput = FrontendRouterInputs['obligation']['insert'];

export const useInsertObligation = createMutationHook({
  name: 'insertObligation',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.obligation.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: ({
    schedule,
    ...rest
  }: InsertObligationInput): InsertObligationTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
    Adherence: rest.Adherence ?? '',
  }),
  invalidate: (trpc) => [
    trpc.frontend.obligation.register.queryKey(),
    trpc.frontend.obligation.getById.queryKey(),
    // COI-889: both siblings declare these — they back the parent-obligation
    // dropdown and the obligation multiselect.
    trpc.frontend.obligation.obligationsByType.queryKey(),
    trpc.frontend.obligation.obligationList.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertObligationMutation => ({
    insertChildObligation: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertObligationDocument,
  graphqlVariables: (input: InsertObligationInput) => ({ object: input }),
  evictFields: ['obligation'],
  refetchQueries: [
    namedOperations.Query.getObligationById,
    namedOperations.Query.getObligationsByType,
  ],
});
